import { useState } from "react";
import { Reveal, Chapter, SectionHead } from "@/components/Reveal";
import { requestPilot } from "@/lib/api";
import { inputCls, labelCls } from "@/lib/modal";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DASHBOARD_IMG =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlbnRlcnByaXNlJTIwc29mdHdhcmUlMjBkYXNoYm9hcmQlMjBkYXJrfGVufDB8fHx8MTc4OTQ1ODQwOXww&ixlib=rb-4.1.0&q=85";

const APPLICATIONS = [
  "Mapping", "Logistics", "Automotive", "Insurance",
  "Smart Cities", "Infrastructure", "Robotics", "AI Systems",
];

const initial = { name: "", company: "", work_email: "", industry: "", interest: "" };

export const Enterprise = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
      const res = await requestPilot(payload);
      setDone(res);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enterprise" className="relative py-28 sm:py-40 bg-ink-950" data-testid="section-enterprise">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter label="Enterprise" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-6">
            <SectionHead
              title={"Turn the physical world into machine-readable intelligence."}
              sub="Fresher, distributed, verified physical-world data for organizations that need to understand changing environments."
            />
            <Reveal delay={0.2}>
              <p className="mt-10 text-sm text-steel" data-testid="enterprise-applications">
                Built for{" "}
                <span className="text-mist/80">
                  {APPLICATIONS.join(" · ")}
                </span>
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="relative mt-10 overflow-hidden rounded-xl" data-testid="enterprise-visual">
                <img
                  src={DASHBOARD_IMG}
                  alt="Enterprise data dashboard consuming Synaphy intelligence"
                  className="h-64 sm:h-80 w-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                <div className="absolute bottom-4 left-4 font-mono text-[9px] tracking-[0.15em] text-zinc-500 uppercase">
                  Data products · planned
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.2}>
              <div id="enterprise-form" data-testid="enterprise-form-card">
                {!done ? (
                  <>
                    <h3 className="font-display text-2xl font-semibold tracking-tight text-mist">
                      Request a pilot
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-steel">
                      Tell us what physical-world data you need. Our enterprise
                      team will reach out.
                    </p>
                    <form onSubmit={submit} className="mt-8 space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="ent-name" className={labelCls}>Name</label>
                          <input id="ent-name" data-testid="ent-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                        </div>
                        <div>
                          <label htmlFor="ent-company" className={labelCls}>Company</label>
                          <input id="ent-company" data-testid="ent-company-input" required value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="ent-email" className={labelCls}>Work email</label>
                        <input id="ent-email" data-testid="ent-email-input" type="email" required value={form.work_email} onChange={set("work_email")} placeholder="you@company.com" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="ent-industry" className={labelCls}>Industry</label>
                        <select
                          id="ent-industry"
                          data-testid="ent-industry-select"
                          required
                          value={form.industry}
                          onChange={set("industry")}
                          className={`${inputCls} appearance-none ${form.industry ? "" : "text-zinc-600"}`}
                        >
                          <option value="" disabled className="bg-ink-900">Select industry</option>
                          {APPLICATIONS.map((a) => (
                            <option key={a} value={a.toUpperCase()} className="bg-ink-900 text-mist">{a}</option>
                          ))}
                          <option value="OTHER" className="bg-ink-900 text-mist">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ent-interest" className={labelCls}>What data are you interested in?</label>
                        <textarea
                          id="ent-interest"
                          data-testid="ent-interest-input"
                          required
                          rows={3}
                          value={form.interest}
                          onChange={set("interest")}
                          placeholder="Road conditions, signage, fleet intelligence..."
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="pilot-submit-btn"
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-signal-cyan px-7 py-3.5 text-sm font-semibold text-ink-950 transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_8px_40px_rgba(34,211,238,0.25)] disabled:opacity-60"
                      >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Explore enterprise
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="py-8" data-testid="pilot-success">
                    <CheckCircle2 className="h-10 w-10 text-signal-green mb-5" />
                    <h3 className="font-display text-2xl font-semibold tracking-tight text-mist">
                      Request received.
                    </h3>
                    <p className="mt-3 text-[15px] text-steel leading-relaxed">
                      Our enterprise team will review your pilot request and reach out.
                    </p>
                    <div className="mt-5 font-mono text-xs tracking-[0.15em] text-signal-cyan" data-testid="pilot-request-id">
                      {done.request_id}
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
